/**
 * QuizIconProgress Component
 *
 * Displays an icon and progress for a quiz/assessment, with a link to the quiz resource.
 * The icon and link adapt based on user access rights and quiz progress state.
 *
 * Features:
 * - Dynamic link generation for quiz resource
 * - Conditional rendering of progress icon and link based on access rights and quiz visibility
 * - Multi-language support via i18n
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing quiz info (idCourse, idVideo, isShowQuiz, evaluation)
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Array} props.userTrack - Array of user tracking objects for quiz progress
 * @returns {JSX.Element} The rendered quiz icon and progress
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import CourseProgress from 'components/UI/CourseProgress/CourseProgress';
import { getLinkLtik } from 'constants/LTIData';
import classes from './QuizIconProgress.module.css';

const url = window.location.pathname.split('/');

const QuizIconProgress = props => {
  const { t } = useTranslation(['course']);
  const [activityState, setActivityState] = useState(
    'without_starting'
  );

  useEffect(() => {
    // Set quiz progress state based on userTrack
    props.userTrack.forEach(track => {
      if (track.id_media === props.data.idVideo) {
        if (track.media_quiz === 2) setActivityState('finished');
        else if (track.media_quiz === 1) setActivityState('started');
        else if (track.media_quiz === 0)
          setActivityState('without_starting');
      }
    });
  }, []);

  return (
    <Link
      to={
        getLinkLtik()
          ? `/q/${props.data.idCourse}/${
              props.data.idVideo
            }/?ltik=${getLinkLtik()}`
          : url[2] === 'a'
          ? `/a/q/${props.data.idCourse}/${props.data.idVideo}`
          : url[2] === 'course'
          ? `/course/q/${props.data.idCourse}/${props.data.idVideo}`
          : `/q/${props.data.idCourse}/${props.data.idVideo}`
      }
      className={`${classes.productLesson} ${
        !props.accessCourse || !props.data.isShowQuiz
          ? classes.productLessonDisabled
          : ''
      }`}
    >
      {/* Only render if quiz evaluation is enabled for this lesson */}
      {props.data.evaluation.evaluation === 1 && (
        <span style={{ fontSize: 14, minHeight: 20 }}>
          <CourseProgress
            activityState={activityState} // Prop passed: quiz progress state
            icon="quiz" // Prop passed: icon type for quiz
          />
          <p className={classes.productContentItemText}>
            {t('assessment')}
            {/* Internationalized label for assessment */}
          </p>
        </span>
      )}
    </Link>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
QuizIconProgress.propTypes = {
  /**
   * Data object containing quiz info (idCourse, idVideo, isShowQuiz, evaluation)
   * Used to generate links and display quiz details
   * Passed as prop to Link, CourseProgress, and text elements
   */
  data: PropTypes.shape({
    idCourse: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    idVideo: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    isShowQuiz: PropTypes.bool,
    evaluation: PropTypes.shape({
      evaluation: PropTypes.number
    })
  }),
  /**
   * Boolean indicating whether the user has access to the course
   * Controls link and icon rendering/styling
   * Passed as prop to Link and CourseProgress
   */
  accessCourse: PropTypes.bool,
  /**
   * Array of user tracking objects for quiz progress
   * Used to determine quiz progress state
   * Passed as prop to internal logic
   */
  userTrack: PropTypes.arrayOf(PropTypes.object)
};

QuizIconProgress.defaultProps = {
  data: {
    idCourse: '',
    idVideo: '',
    isShowQuiz: false,
    evaluation: { evaluation: 0 }
  },
  accessCourse: false,
  userTrack: []
};

export default QuizIconProgress;
